import React, { useEffect, useRef, useState } from "react";
import { MapPin, Briefcase, Calendar, CheckCircle2 } from "lucide-react";
import { aboutData, personalInfo } from "../data/mock";

function useReveal(cls = "reveal") {
  const ref = useRef();
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function About() {
  const headerRef = useReveal();
  const leftRef = useReveal();
  const rightRef = useReveal("reveal-right");

  return (
    <div style={{ padding: "120px 0", position: "relative", overflow: "hidden", background: "var(--surface)" }}>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>

        {/* Section header — editorial style */}
        <div ref={headerRef} className="reveal" style={{ marginBottom: 80, display: "flex", alignItems: "flex-end", gap: 24, position: "relative" }}>
          <div className="ghost-number" style={{ position: "absolute", left: -20, top: -60, zIndex: 0, lineHeight: 1 }}>01</div>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6a6a88", letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>TENTANG SAYA</div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(36px, 6vw, 72px)", fontWeight: 700, color: "#f2f2ff", margin: 0, letterSpacing: "-2px", lineHeight: 1 }}>
              Kenapa Pilih<br />
              <span style={{ background: "linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)", backgroundSize: "400% 400%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: "holo-shift 5s ease infinite" }}>Saya?</span>
            </h2>
          </div>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)", marginBottom: 12 }} />
        </div>

        {/* Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "start" }} className="about-grid">

          {/* Left */}
          <div ref={leftRef} className="reveal">
            {/* Profile */}
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32, paddingBottom: 32, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ width: 72, height: 72, borderRadius: 4, background: "linear-gradient(135deg, #00e5ff, #9b00ff, #ff0099)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 22, color: "#07070f", flexShrink: 0 }}>EP</div>
              <div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 18, color: "#f2f2ff" }}>{personalInfo.name}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6a6a88", letterSpacing: 1, marginBottom: 4 }}>{personalInfo.title.toUpperCase()}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <MapPin size={12} color="#6a6a88" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6a6a88" }}>{personalInfo.location}</span>
                </div>
              </div>
            </div>

            {aboutData.bio.map((p, i) => (
              <p key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#9090a8", lineHeight: 1.9, margin: "0 0 16px" }}>{p}</p>
            ))}

            {/* Strengths */}
            <div style={{ marginTop: 32 }}>
              {["Respon cepat & komunikasi jelas","Solusi efisien sesuai anggaran","Garansi kualitas & purna jual","Berpengalaman lintas industri"].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 2, background: "linear-gradient(135deg,#00e5ff,#9b00ff)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CheckCircle2 size={11} color="#07070f" strokeWidth={3} />
                  </div>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#b0b0c8" }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Timeline */}
          <div ref={rightRef} className="reveal-right">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 36 }}>
              <Briefcase size={18} color="#00e5ff" />
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 18, color: "#f2f2ff" }}>Pengalaman Kerja</span>
            </div>

            {aboutData.experience.map((exp, i) => (
              <div key={exp.id} style={{ display: "flex", gap: 20, marginBottom: i < aboutData.experience.length - 1 ? 32 : 0 }}>
                {/* Timeline */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 4 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                    background: i === 0 ? "linear-gradient(135deg,#00e5ff,#9b00ff)" : "transparent",
                    border: i === 0 ? "none" : "1px solid rgba(255,255,255,0.2)",
                  }} />
                  {i < aboutData.experience.length - 1 && <div style={{ width: 1, flex: 1, background: "rgba(255,255,255,0.06)", marginTop: 8 }} />}
                </div>
                {/* Content */}
                <div style={{
                  flex: 1, padding: "18px 20px",
                  background: "var(--card)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 4,
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "rgba(0,229,255,0.2)";
                  e.currentTarget.style.background = "linear-gradient(var(--card),var(--card)) padding-box, linear-gradient(135deg,#00e5ff,#9b00ff) border-box";
                  e.currentTarget.style.borderColor = "transparent";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "var(--card)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                }}>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 15, color: "#f2f2ff", marginBottom: 3 }}>{exp.role}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#00e5ff", letterSpacing: 0.5, marginBottom: 8 }}>{exp.company}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                    <Calendar size={11} color="#6a6a88" />
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6a6a88" }}>{exp.period}</span>
                  </div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6a6a88", lineHeight: 1.7, margin: 0 }}>{exp.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
